import { Guid } from "guid-typescript";
import _ from "lodash";
import { KMainFunctions } from "./main-functions";

export interface IFolderBase {
  id: string;
  name: string;
  parentFolderId?: string;
  children?: IFolderBase[];
}

export interface IFolderWithItems<T = string> extends IFolderBase {
  itemIds?: T[];
}

export class FolderHelper {
  static addFolder = <T extends IFolderBase>(
    folders: T[],
    folderName: string,
    additionalProps: Partial<T> = {}
  ): { success: boolean; folders: T[]; folderId?: string } => {
    if (folders.some((x) => x.name === folderName)) {
      KMainFunctions.displayWarningNotification("This folder name already exists.");
      return { success: false, folders };
    }

    const id = Guid.create().toString();
    const newFolder = _.cloneDeep({
      id,
      name: folderName,
      ...additionalProps
    } as T);

    const updatedFolders = [...folders, newFolder];
    return { success: true, folders: updatedFolders, folderId: id };
  };

  static deleteFolder = <T extends IFolderBase>(folders: T[], folderId: string): T[] => {
    return folders.filter((x) => x.id !== folderId);
  };

  static editFolder = <T extends IFolderBase>(folders: T[], folderId: string, folderName: string): T[] => {
    return folders.map((x) => {
      if (x.id === folderId) {
        return { ...x, name: folderName };
      }
      return x;
    });
  };

  static addItemToFolder = <T extends IFolderWithItems<ItemType>, ItemType = string>(
    folders: T[],
    folderId: string,
    itemId: ItemType,
    itemKey: keyof T = "itemIds" as keyof T
  ): T[] => {
    return folders.map((folder) => {
      if (folder.id === folderId) {
        const items = (folder[itemKey] as ItemType[] | undefined) || [];
        if (!items.includes(itemId)) {
          return {
            ...folder,
            [itemKey]: [...items, itemId]
          };
        }
      } else {
        const items = (folder[itemKey] as ItemType[] | undefined) || [];
        return {
          ...folder,
          [itemKey]: items.filter((id) => id !== itemId)
        };
      }
      return folder;
    });
  };

  static removeItemFromFolder = <T extends IFolderWithItems<ItemType>, ItemType = string>(
    folders: T[],
    folderId: string,
    itemId: ItemType,
    itemKey: keyof T = "itemIds" as keyof T
  ): T[] => {
    return folders.map((folder) => {
      if (folder.id === folderId) {
        const items = (folder[itemKey] as ItemType[] | undefined) || [];
        return {
          ...folder,
          [itemKey]: items.filter((id) => id !== itemId)
        };
      }
      return folder;
    });
  };

  static addChildFolder = <T extends IFolderBase>(
    folders: T[],
    parentFolderId: string,
    folderName: string,
    additionalProps: Partial<T> = {}
  ): { success: boolean; folders: T[]; folderId?: string } => {
    const parent = folders.find((x) => x.id === parentFolderId);
    if (!parent) {
      return { success: false, folders };
    }

    if (!parent.children) {
      parent.children = [];
    }

    if (parent.children.some((x) => x.name === folderName)) {
      KMainFunctions.displayWarningNotification("This folder name already exists.");
      return { success: false, folders };
    }

    const id = Guid.create().toString();
    const newChild = _.cloneDeep({
      id,
      name: folderName,
      parentFolderId,
      ...additionalProps
    } as T);

    parent.children.push(newChild as IFolderBase);
    return { success: true, folders, folderId: id };
  };

  static deleteChildFolder = <T extends IFolderBase>(
    folders: T[],
    parentFolderId: string,
    childFolderId: string
  ): T[] => {
    return folders.map((folder) => {
      if (folder.id === parentFolderId && folder.children) {
        return {
          ...folder,
          children: folder.children.filter((x) => x.id !== childFolderId)
        };
      }
      return folder;
    });
  };

  static editChildFolder = <T extends IFolderBase>(
    folders: T[],
    parentFolderId: string,
    childFolderId: string,
    newFolderName: string
  ): T[] => {
    return folders.map((folder) => {
      if (folder.id === parentFolderId && folder.children) {
        return {
          ...folder,
          children: folder.children.map((x) => {
            if (x.id === childFolderId) {
              return { ...x, name: newFolderName };
            }
            return x;
          })
        };
      }
      return folder;
    });
  };

  static addItemToChildFolder = <T extends IFolderBase, ItemType = string>(
    folders: T[],
    parentFolderId: string,
    childFolderId: string,
    itemId: ItemType,
    itemKey: string = "itemIds"
  ): T[] => {
    const allFolders = FolderHelper.removeItemFromAllFolders(folders, itemId, itemKey);

    return allFolders.map((folder) => {
      if (folder.id === parentFolderId && folder.children) {
        return {
          ...folder,
          children: folder.children.map((child: any) => {
            if (child.id === childFolderId) {
              const items = (child[itemKey] as ItemType[] | undefined) || [];
              return {
                ...child,
                [itemKey]: [...items, itemId]
              };
            }
            return child;
          })
        };
      }
      return folder;
    });
  };

  static removeItemFromChildFolder = <T extends IFolderBase, ItemType = string>(
    folders: T[],
    parentFolderId: string,
    childFolderId: string,
    itemId: ItemType,
    itemKey: string = "itemIds"
  ): T[] => {
    return folders.map((folder) => {
      if (folder.id === parentFolderId && folder.children) {
        return {
          ...folder,
          children: folder.children.map((child: any) => {
            if (child.id === childFolderId) {
              const items = (child[itemKey] as ItemType[] | undefined) || [];
              return {
                ...child,
                [itemKey]: items.filter((id) => id !== itemId)
              };
            }
            return child;
          })
        };
      }
      return folder;
    });
  };

  static removeItemFromAllFolders = <T extends IFolderBase, ItemType = string>(
    folders: T[],
    itemId: ItemType,
    itemKey: string = "itemIds"
  ): T[] => {
    return folders.map((folder: any) => {
      const items = (folder[itemKey] as ItemType[] | undefined) || [];
      const updatedFolder = {
        ...folder,
        [itemKey]: items.filter((id) => id !== itemId)
      };

      if (folder.children && folder.children.length > 0) {
        updatedFolder.children = FolderHelper.removeItemFromAllFolders(folder.children, itemId, itemKey);
      }

      return updatedFolder;
    });
  };

  static getFolderById = <T extends IFolderBase>(folderId: string, folders: T[]): T | undefined => {
    if (!folders || folders.length === 0) {
      return undefined;
    }

    const queue = [...folders];

    while (queue.length > 0) {
      const folder = queue.shift()!;

      if (folder.id === folderId) {
        return folder;
      }

      if (folder.children && folder.children.length > 0) {
        queue.push(...(folder.children as T[]));
      }
    }

    return undefined;
  };

  static getFolderWithParent = <T extends IFolderBase>(
    folderId: string,
    folders: T[]
  ): { folder: T; parent: T | null } | undefined => {
    if (!folders || folders.length === 0) {
      return undefined;
    }

    for (const topFolder of folders) {
      if (topFolder.id === folderId) {
        return { folder: topFolder, parent: null };
      }

      if (topFolder.children && Array.isArray(topFolder.children)) {
        for (const child of topFolder.children) {
          if (child.id === folderId) {
            return { folder: child as T, parent: topFolder };
          }
        }
      }
    }

    return undefined;
  };

  static findFolderByItemId = <T extends IFolderBase, ItemType = string>(
    itemId: ItemType,
    folders: T[],
    itemKey: string = "itemIds"
  ): T | undefined => {
    if (!folders || folders.length === 0) {
      return undefined;
    }

    const queue = [...folders];

    while (queue.length > 0) {
      const folder: any = queue.shift()!;

      const items = (folder[itemKey] as ItemType[] | undefined) || [];
      if (items.includes(itemId)) {
        return folder;
      }

      if (folder.children && folder.children.length > 0) {
        queue.push(...folder.children);
      }
    }

    return undefined;
  };
}
